import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import constant from 'lodash/constant'
import isFinite from 'lodash/isFinite'
import toString from 'lodash/toString'
import { ROUTE_DISBURSE, ROUTE_DISBURSEMENTS } from '@klear/constants'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { usePathSyncedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import { DISBURSEMENT_STEP_ID, DISBURSEMENT_STEP_LABELS, type DisbursementStepId } from '@/app/disbursements/steps'
import {
  useDisbursementOutletContext,
  type DisbursementOutletContext,
  type DisbursementStepCtx
} from '@/app/disbursements/outlet-context'
import SelectTraderStep from '@/app/disbursements/steps/SelectTraderStep'
import SelectItemsStep from '@/app/disbursements/steps/SelectItemsStep'
import ReviewStep from '@/app/disbursements/steps/ReviewStep'

const selectTraderModule: WizardStepModule<DisbursementStepCtx> = {
  id: DISBURSEMENT_STEP_ID.SELECT_TRADER,
  label: DISBURSEMENT_STEP_LABELS[DISBURSEMENT_STEP_ID.SELECT_TRADER],
  isComplete: (ctx) => ctx.state.traderId != null,
  component: SelectTraderStep
}

const selectItemsModule: WizardStepModule<DisbursementStepCtx> = {
  id: DISBURSEMENT_STEP_ID.SELECT_ITEMS,
  label: DISBURSEMENT_STEP_LABELS[DISBURSEMENT_STEP_ID.SELECT_ITEMS],
  isComplete: (ctx) => ctx.state.selectedAdvanceIds.length > 0,
  component: SelectItemsStep
}

const reviewModule: WizardStepModule<DisbursementStepCtx> = {
  id: DISBURSEMENT_STEP_ID.REVIEW,
  label: DISBURSEMENT_STEP_LABELS[DISBURSEMENT_STEP_ID.REVIEW],
  isComplete: constant(false),
  component: ReviewStep
}

const DISBURSEMENT_STEP_MODULES: Array<WizardStepModule<DisbursementStepCtx>> = [
  selectTraderModule,
  selectItemsModule,
  reviewModule
]

function resolveInitialStepId(stepIdParam: string | undefined, outlet: DisbursementOutletContext): DisbursementStepId {
  const firstStep = outlet.stepOrder[0] ?? DISBURSEMENT_STEP_ID.SELECT_TRADER
  if (!stepIdParam || !outlet.stepOrder.includes(stepIdParam as DisbursementStepId)) return firstStep
  const requested = stepIdParam as DisbursementStepId
  if (requested === firstStep) return requested
  const needsTrader = requested === DISBURSEMENT_STEP_ID.SELECT_ITEMS || requested === DISBURSEMENT_STEP_ID.REVIEW
  if (needsTrader && outlet.state.traderId == null) return firstStep
  return requested
}

function buildStepQuery(traderId: number | null): string {
  const params = new URLSearchParams()
  if (traderId != null && isFinite(traderId) && traderId > 0) {
    params.set('traderId', toString(traderId))
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export type DisbursementFlowProps = {
  onNavStateChange: (nav: WizardStepGroupNavState) => void
}

export function DisbursementFlow({ onNavStateChange }: DisbursementFlowProps) {
  const navigate = useNavigate()
  const { stepId: stepIdParam } = useParams<{ stepId?: string }>()
  const outlet = useDisbursementOutletContext()

  const stepDefs = useMemo(() => bindWizardSteps(DISBURSEMENT_STEP_MODULES, outlet), [outlet])

  const syncPathStepId = useCallback(
    (stepId: string) => {
      navigate(`/${ROUTE_DISBURSEMENTS}/${ROUTE_DISBURSE}/${stepId}${buildStepQuery(outlet.state.traderId)}`, {
        replace: true
      })
    },
    [navigate, outlet.state.traderId]
  )

  const flow = usePathSyncedWizardFlow(stepDefs, {
    pathStepId: stepIdParam,
    resolveInitialStepId: (pathStepId) => resolveInitialStepId(pathStepId, outlet),
    syncPathStepId,
    stepSelection: 'gated',
    onNavStateChange,
    onExitFirstStep: () => navigate(`/${ROUTE_DISBURSEMENTS}`)
  })

  return (
    <WizardFlowContent
      flow={flow}
      className="w-full max-w-6xl space-y-4"
      onSubmit={() => void outlet.disburse()}
      cancelOnFirstStep
      disabled={outlet.isDisbursing}
      submitDisabled={outlet.state.selectedAdvanceIds.length === 0 || outlet.hasMixedCurrency || outlet.isDisbursing}
      submitLabel={outlet.isDisbursing ? 'Disbursing…' : 'Disburse advances'}
      navClassName="flex justify-between gap-2 pt-4"
    />
  )
}
